import Dropzone from "react-dropzone";
import { useFileUpload } from "@/lib/FileUpload/Hooks/useFileUpload";
import { ComponentProps, useState } from "react";
import { cn } from "@/lib/utils";
import { UploadIcon, AlertCircle, CheckCircle, X } from "lucide-react";
import { IMAGE_ACCEPT } from "@/lib/FileUpload/accept";

export type Upload = { file: File; filePath: string };

type FileUploaderProps = {
  onDrop?: (files: File[]) => void;
  onUpload: (uploads: Upload[]) => Promise<void> | void;
  isDisabled?: boolean;
  className?: string;
  compact?: boolean;
  maxSize?: number;
  maxFileCount?: number;
  fileTypeLabel?: string;
} & Partial<ComponentProps<typeof Dropzone>>;

export function FileUploader({
  onUpload,
  onDrop,
  isDisabled,
  className,
  compact = false,
  maxSize = 1024 * 1024 * 4, // 4MB default
  maxFileCount = 1,
  fileTypeLabel = "Supports JPEG, PNG, WebP, and AVIF formats",
  accept = IMAGE_ACCEPT,
  ...props
}: FileUploaderProps) {
  const { mutateAsync } = useFileUpload();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const validateFiles = (files: File[]): string | null => {
    if (files.length > maxFileCount) {
      return `Too many files. Maximum ${maxFileCount} file${maxFileCount > 1 ? "s" : ""} allowed.`;
    }

    for (const file of files) {
      if (file.size > maxSize) {
        return `File "${file.name}" is too large. Maximum size is ${formatBytes(maxSize)}.`;
      }
    }

    return null;
  };

  const handleDrop = async (acceptedFiles: File[], rejectedFiles: any[]) => {
    setError(null);
    setSuccess(false);

    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors?.some((e: any) => e.code === "file-invalid-type")) {
        setError(`Invalid file type. ${fileTypeLabel}`);
        return;
      }
      if (rejection.errors?.some((e: any) => e.code === "file-too-large")) {
        setError(`File is too large. Maximum size is ${formatBytes(maxSize)}.`);
        return;
      }
    }

    // Validate accepted files
    const validationError = validateFiles(acceptedFiles);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (acceptedFiles.length === 0) return;

    try {
      setIsLoading(true);
      onDrop?.(acceptedFiles);
      const files = await mutateAsync(acceptedFiles);
      await onUpload(files);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000); // Hide success after 3 seconds
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "Upload failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => setError(null);

  return (
    <div className="w-full space-y-3">
      <Dropzone
        accept={accept}
        onDrop={handleDrop}
        disabled={isLoading || isDisabled}
        noClick={isLoading}
        maxSize={maxSize}
        maxFiles={maxFileCount}
        {...props}
      >
        {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
          <div
            {...getRootProps()}
            className={cn(
              "relative grid h-64 w-full cursor-pointer place-items-center rounded-xl border-2 border-dashed px-6 py-8 text-center transition-all duration-200 ease-in-out",
              compact && "h-auto px-4 py-3 text-left",
              "ring-offset-background focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              // Default state
              "border-border/40 bg-background hover:border-border/60 hover:bg-muted/30",
              // Drag states
              isDragActive &&
                !isDragReject &&
                "border-primary/60 bg-primary/5 scale-[1.02]",
              isDragReject && "border-destructive/60 bg-destructive/5",
              // Loading state
              isLoading && "border-primary/40 bg-primary/5 cursor-wait",
              // Success state
              success && "border-green-500/60 bg-green-50/50",
              // Error state
              error && "border-destructive/60 bg-destructive/5",
              // Disabled state
              (isDisabled || isLoading) && "pointer-events-none opacity-60",
              className,
            )}
          >
            <input {...getInputProps()} />

            <div
              className={cn(
                "flex flex-col items-center justify-center gap-6",
                compact && "flex-row gap-3",
              )}
            >
              {/* Icon */}
              <div
                className={cn(
                  "rounded-full border-2 border-dashed p-4 transition-all duration-200",
                  compact && "p-2",
                  isDragActive &&
                    !isDragReject &&
                    "border-primary/60 bg-primary/10 scale-110",
                  isDragReject && "border-destructive/60 bg-destructive/10",
                  isLoading && "border-primary/40 animate-pulse",
                  success && "border-green-500/60 bg-green-50",
                  error && "border-destructive/60 bg-destructive/10",
                )}
              >
                {isLoading ? (
                  <div className="border-primary size-8 animate-spin rounded-full border-2 border-t-transparent" />
                ) : success ? (
                  <CheckCircle
                    className="size-8 text-green-600"
                    aria-hidden="true"
                  />
                ) : error ? (
                  <AlertCircle
                    className="text-destructive size-8"
                    aria-hidden="true"
                  />
                ) : (
                  <UploadIcon
                    className={cn(
                      "size-8 transition-colors duration-200",
                      compact && "size-5",
                      isDragActive && !isDragReject && "text-primary",
                      isDragReject && "text-destructive",
                      !isDragActive && "text-gray-500",
                    )}
                    aria-hidden="true"
                  />
                )}
              </div>

              {/* Text content */}
              <div className={cn("space-y-2", compact && "space-y-0")}>
                {isLoading ? (
                  <div className="space-y-1">
                    <p className="text-primary font-medium">Uploading...</p>
                    <p className="text-sm text-gray-500">
                      Please wait while we process your file
                    </p>
                  </div>
                ) : success ? (
                  <div className="space-y-1">
                    <p className="font-medium text-green-700">
                      Upload successful!
                    </p>
                    <p className="text-sm text-green-600">
                      Your file has been uploaded
                    </p>
                  </div>
                ) : isDragActive && !isDragReject ? (
                  <div className="space-y-1">
                    <p className="text-primary font-medium">
                      Drop your files here
                    </p>
                    <p className="text-sm text-gray-500">Release to upload</p>
                  </div>
                ) : isDragReject ? (
                  <div className="space-y-1">
                    <p className="text-destructive font-medium">
                      Invalid file type
                    </p>
                    <p className="text-destructive/80 text-sm">
                      {fileTypeLabel}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-foreground font-medium">
                      Drop files here or click to browse
                    </p>
                    <p className="text-sm leading-relaxed text-gray-500">
                      {maxFileCount === 1 ? (
                        <>Maximum file size {formatBytes(maxSize)}</>
                      ) : (
                        <>
                          Upload up to{" "}
                          {maxFileCount === Infinity
                            ? "multiple"
                            : maxFileCount}{" "}
                          files, {formatBytes(maxSize)} each
                        </>
                      )}
                    </p>
                    {compact ? null : (
                      <p className="text-xs text-gray-500/80">
                        {fileTypeLabel}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Dropzone>

      {/* Error message */}
      {error && (
        <div className="border-destructive/20 bg-destructive/5 flex items-start gap-3 rounded-lg border p-4">
          <AlertCircle className="text-destructive mt-0.5 size-5 flex-shrink-0" />
          <div className="flex-1 space-y-1">
            <p className="text-destructive text-sm font-medium">Upload Error</p>
            <p className="text-destructive/80 text-sm">{error}</p>
          </div>
          <button
            type="button"
            onClick={clearError}
            className="text-destructive/60 hover:text-destructive transition-colors"
            aria-label="Dismiss error"
          >
            <X className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: "accurate" | "normal";
  } = {},
) {
  const { decimals = 0, sizeType = "normal" } = opts;

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === "accurate"
      ? (accurateSizes[i] ?? "Bytes")
      : (sizes[i] ?? "Bytes")
  }`;
}
